import React, { useEffect, useState } from 'react';
import { SubTabs, useAdminGet } from '../kit';
import { EMAIL_USE_DESIGN_EVENT } from './designs/types';
import { CampaignHistory } from './campaigns/CampaignHistory';
import { Composer, Handoff } from './campaigns/Composer';
import { SendersEditor } from './campaigns/SendersEditor';
import { TagFunnel } from './campaigns/TagFunnel';

/**
 * E-mail campaigns (backend: admin_email_targeting) — the Email Center "Campaigns" tab.
 *
 *   New campaign      audience (a filtered group with every person listed and
 *                     ticked, or people picked one by one) → content (a
 *                     template, or written here) → preview → typed-count send
 *   Campaigns         every campaign as a record, with its funnel
 *   Funnel by tag     the same funnel for everyone carrying a tag
 *   Approved senders  the From addresses a campaign may use
 *
 * Hand-off from the People directory: dispatch
 *   window.dispatchEvent(new CustomEvent('utech:email-people', { detail: { user_ids: [...] } }))
 * and this tab opens New campaign → Specific people with them picked
 * (optional detail.template preselects that template, detail.source names
 * who handed them over, e.g. Leads → Prospects). The
 * event may arrive before the tab is mounted (the directory switches tab and
 * dispatches at once), so the latest one is kept here until it mounts.
 *
 * Deep link (2026-10-05): /admin?group=email&tab=campaigns&campaign=<send_id>
 * reaches here as `linked` and opens that campaign's drawer in Campaigns.
 */

export const EMAIL_PEOPLE_EVENT = 'utech:email-people';

type HandoffIn = { ids: string[]; template?: string; source?: string };
let pendingHandoff: HandoffIn | null = null;
const handoffListeners = new Set<(h: HandoffIn) => void>();
if (typeof window !== 'undefined') {
  window.addEventListener(EMAIL_PEOPLE_EVENT, (e: Event) => {
    const d = (e as CustomEvent<{ user_ids?: unknown; template?: unknown; source?: unknown }>).detail;
    const ids = d?.user_ids;
    if (!Array.isArray(ids)) return;
    const clean = ids.map(x => String(x ?? '').trim()).filter(Boolean);
    if (!clean.length) return;
    const h: HandoffIn = { ids: clean,
      template: typeof d?.template === 'string' && d.template ? d.template : undefined,
      source: typeof d?.source === 'string' && d.source ? d.source : undefined };
    if (handoffListeners.size) handoffListeners.forEach(f => f(h));
    else pendingHandoff = h;
  });
}

const VIEWS = ['New campaign', 'Campaigns', 'Funnel by tag', 'Approved senders'];

const SendToUsers: React.FC<{ linked?: { id: string; nonce: string } | null }> = ({ linked }) => {
  const [view, setView] = useState(linked?.id ? VIEWS[1] : VIEWS[0]);
  const [handoff, setHandoff] = useState<Handoff | null>(null);
  const [openCampaign, setOpenCampaign] = useState<string | null>(linked?.id || null);
  useEffect(() => {
    if (!linked?.id) return;
    setView(VIEWS[1]);
    setOpenCampaign(linked.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linked?.nonce]);
  const [sendersVersion, setSendersVersion] = useState(0);
  const tplList = useAdminGet('email', 'editable_templates');
  const tplName = (k: string) =>
    ((tplList.data as any)?.templates || []).find((t: any) => String(t.key) === k)?.name || k;

  useEffect(() => {
    const take = (x: HandoffIn) => {
      setView(VIEWS[0]);
      setHandoff(h => ({ ids: x.ids, template: x.template, source: x.source, seq: (h?.seq || 0) + 1 }));
    };
    if (pendingHandoff) {
      take(pendingHandoff);
      pendingHandoff = null;
    }
    handoffListeners.add(take);
    return () => { handoffListeners.delete(take); };
  }, []);
  useEffect(() => {
    const toComposer = () => setView(VIEWS[0]);
    window.addEventListener(EMAIL_USE_DESIGN_EVENT, toComposer);
    return () => window.removeEventListener(EMAIL_USE_DESIGN_EVENT, toComposer);
  }, []);

  return (
    <div className="flex flex-col gap-4">
      <SubTabs tabs={VIEWS} active={view} onChange={setView} />
      {/* Kept mounted while another view is open, so a half-written campaign survives a look at the history. */}
      <div className={view === VIEWS[0] ? '' : 'hidden'}>
        <Composer handoff={handoff} sendersVersion={sendersVersion} tplList={tplList}
          onSent={id => { setOpenCampaign(id); setView(VIEWS[1]); }} />
      </div>
      {view === VIEWS[1] && <CampaignHistory open={openCampaign} onOpen={setOpenCampaign} tplName={tplName} />}
      {view === VIEWS[2] && <TagFunnel />}
      {view === VIEWS[3] && <SendersEditor onSaved={() => setSendersVersion(v => v + 1)} />}
    </div>
  );
};

export default SendToUsers;
